import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { 
  MessageSquare, 
  Sun, 
  Moon, 
  Loader2, 
  Building2, 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  ArrowRight,
  Sparkles,
  Zap,
  TrendingUp,
  ShieldCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useThemeStore } from '@/store/themeStore';
import { motion } from 'motion/react';

export function Login() {
  const { isDarkMode, toggleDarkMode } = useThemeStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showGoogleOAuthInfo, setShowGoogleOAuthInfo] = useState(false);
  
  const { login, loading, error, setError, session, canAccessApp } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.removeItem('impersonation_backup');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkTermsAndRedirect = async () => {
      const identityIsCurrent = () => !cancelled && useAuthStore.getState().session?.user?.id === session?.user?.id;
      if (session && canAccessApp) {
        { // Read acceptance from the authenticated profile, never legacy localStorage.
          // FIX-TERMOS: também checar no banco — se já aceitou em outro dispositivo, não pedir de novo
          try {
            const token = session.access_token;
            if (token) {
              const profileRes = await fetch('/api/profile', {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (profileRes.ok) {
                const profileData = await profileRes.json();
                if (!identityIsCurrent()) return;
                if (profileData?.termos_aceitos_em) {

                  navigate('/', { replace: true });
                  return;
                }
              }
            }
          } catch (e) {
            console.warn('[LOGIN] Erro ao checar termos no banco:', e);
          }
          if (!identityIsCurrent()) return;
          navigate('/termos', { replace: true });
        }
      }
    };
    checkTermsAndRedirect();
    return () => { cancelled = true; };
  }, [session, canAccessApp, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim()) {
      setError('Por favor, preencha todos os campos.');
      return;
    }

    const res = await login(email.trim(), password);
    if (res.success) {
      const submittedUserId = useAuthStore.getState().session?.user?.id;
      const identityIsCurrent = () => !!submittedUserId && useAuthStore.getState().session?.user?.id === submittedUserId;
      { // Read acceptance from the authenticated profile, never legacy localStorage.
        // FIX-TERMOS: também checar no banco — se já aceitou em outro dispositivo, não pedir de novo
        try {
          const token = useAuthStore.getState().session?.access_token;
          if (token) {
            const profileRes = await fetch('/api/profile', {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (profileRes.ok) {
              const profileData = await profileRes.json();
                if (!identityIsCurrent()) return;
              if (profileData?.termos_aceitos_em) {

                navigate('/', { replace: true });
                return;
              }
            }
          }
        } catch (e) {
          console.warn('[LOGIN] Erro ao checar termos no banco:', e);
        }
        if (!identityIsCurrent()) return;
          navigate('/termos');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex transition-colors duration-300">
      
      {/* Esquerda: Painel Institucional/Branding (Visível apenas em telas grandes) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0B141A] dark:bg-[#080F14] text-white overflow-hidden flex-col justify-between p-12 border-r border-gray-800">
        
        {/* Elemento Decorativo Sutil de Fundo */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] -mr-40 -mt-40 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[100px] -ml-20 -mb-20 pointer-events-none" />

        {/* Logo / Nome do App */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="p-2.5 bg-blue-600/10 border border-blue-500/20 rounded-xl text-blue-400">
            <MessageSquare className="w-6 h-6" />
          </div>
          <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
            TASK CRM
          </span>
        </div>

        {/* Conteúdo Central: Recursos da Plataforma */}
        <div className="relative z-10 my-auto max-w-lg space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Plataforma Inteligente Multi-Tenant</span>
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight leading-tight text-white">
              Sua central inteligente de atendimento e vendas.
            </h1>
            <p className="text-gray-400 text-sm">
              Gerencie seus leads, agende compromissos, automatize respostas e impulsione a produtividade de múltiplos times em um só lugar.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-emerald-400">
                <ShieldCheck className="w-5 h-5 shrink-0" />
                <span className="text-sm font-bold text-gray-200">Ambiente Seguro</span>
              </div>
              <p className="text-xs text-gray-400">Dados protegidos por criptografia de ponta a ponta e controle rígido de acesso.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-blue-400">
                <Zap className="w-5 h-5 shrink-0" />
                <span className="text-sm font-bold text-gray-200">WhatsApp integrado</span>
              </div>
              <p className="text-xs text-gray-400">Conexão direta para recebimento e disparo automatizado de conversas e mídias.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-indigo-400">
                <TrendingUp className="w-5 h-5 shrink-0" />
                <span className="text-sm font-bold text-gray-200">Funil Visual</span>
              </div>
              <p className="text-xs text-gray-400">Kanban de alta performance para gerenciar cada etapa da jornada de compra.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-amber-400">
                <MessageSquare className="w-5 h-5 shrink-0" />
                <span className="text-sm font-bold text-gray-200">Multiempresas</span>
              </div>
              <p className="text-xs text-gray-400">Separação nativa por lojas, filiais e múltiplos canais independentes.</p>
            </div>
          </div>
        </div>

        {/* Rodapé do Painel de Branding */}
        <div className="relative z-10 text-xs text-gray-500">
          © {new Date().getFullYear()} TASK CRM. Todos os direitos reservados.
        </div>
      </div>

      {/* Direita: Formulário de Login (Mobile ocupa tela cheia) */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center relative px-6 py-12 sm:px-12 lg:px-16">
        
        {/* Toggle de Tema Claro/Escuro */}
        <div className="absolute top-6 right-6">
          <button 
            onClick={() => toggleDarkMode()}
            type="button"
            aria-label="Alternar tema"
            className="p-2.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-xl transition-all bg-white dark:bg-[#111B21] shadow-xs border border-gray-200 dark:border-gray-800 hover:scale-105 active:scale-95 cursor-pointer"
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Container do Formulário */}
        <div className="mx-auto w-full max-w-md space-y-8">
          
          {/* Header do Formulário (Exibe a marca e finalidade do aplicativo em todos os dispositivos para conformidade) */}
          <div className="text-center space-y-4">
            <div className="flex justify-center text-blue-500 dark:text-blue-400 mb-1">
              <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center gap-2.5">
                <MessageSquare className="w-8 h-8" />
                <span className="text-2xl font-black tracking-wider text-gray-950 dark:text-white">
                  TASK CRM
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                Seja bem-vindo de volta
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed max-w-sm mx-auto">
                O <strong>TASK CRM</strong> é uma plataforma integrada para gestão de relacionamento com o cliente, permitindo gerenciar leads, automatizar conversas de WhatsApp e sincronizar eventos com o <strong>Google Calendar</strong>.
              </p>
            </div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white dark:bg-[#111B21] py-8 px-6 shadow-md rounded-2xl border border-gray-200/80 dark:border-gray-800 transition-colors"
          >
            <form className="space-y-5" onSubmit={handleSubmit}>
              
              {/* Campo: E-mail */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  E-mail institucional
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3.5 text-gray-400 dark:text-gray-500">
                    <Mail size={18} />
                  </span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="exemplo@empresa.com"
                    className="w-full pl-11 pr-4 py-2.5 bg-gray-50/50 dark:bg-[#1f2c34]/50 border border-gray-200 dark:border-gray-850 focus:border-blue-500 dark:focus:border-blue-500 focus:bg-white dark:focus:bg-[#111B21] rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-550 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>

              {/* Campo: Senha */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Sua senha
                  </label>
                </div>
                <div className="relative flex items-center">
                  <span className="absolute left-3.5 text-gray-400 dark:text-gray-500">
                    <Lock size={18} />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-11 pr-11 py-2.5 bg-gray-50/50 dark:bg-[#1f2c34]/50 border border-gray-200 dark:border-gray-850 focus:border-blue-500 dark:focus:border-blue-500 focus:bg-white dark:focus:bg-[#111B21] rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-550 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg focus:outline-none cursor-pointer"
                    title={showPassword ? "Ocultar senha" : "Exibir senha"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Mensagem de Erro com Animação */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="p-3 bg-rose-50 dark:bg-rose-950/25 border border-rose-200 dark:border-rose-900/30 rounded-xl text-sm text-rose-600 dark:text-rose-400"
                >
                  {error}
                </motion.div>
              )}

              {/* Botão de Envio */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 transition-all cursor-pointer shadow-md hover:shadow-lg hover:shadow-blue-500/10"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Conectando...</span>
                  </>
                ) : (
                  <>
                    <span>Entrar na Conta</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Footer de Cadastro */}
              <div className="pt-4 text-center border-t border-gray-100 dark:border-gray-800/80">
                <p className="text-xs text-gray-500 dark:text-gray-450">
                  Ainda não tem uma conta para sua empresa?
                </p>
                <Link
                  to="/signup"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  <Building2 size={14} />
                  <span>Cadastrar nova empresa no TASK CRM</span>
                </Link>
              </div>
            </form>
          </motion.div>

          {/* Rodapé Informativo / Requisitos de Consentimento Google OAuth */}
          <div className="bg-gray-150/40 dark:bg-[#152026]/40 rounded-2xl p-4 border border-gray-200/60 dark:border-gray-800/60 transition-all">
            <button
              type="button"
              onClick={() => setShowGoogleOAuthInfo(!showGoogleOAuthInfo)}
              className="w-full flex items-center justify-between text-left cursor-pointer group focus:outline-none"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-500 dark:text-blue-400 shrink-0" />
                <span>Transparência &amp; Uso de Dados Google</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 font-bold group-hover:opacity-80 transition-opacity">
                <span>{showGoogleOAuthInfo ? 'Ocultar' : 'Expandir'}</span>
                {showGoogleOAuthInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </button>
            
            {showGoogleOAuthInfo && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-800/50 space-y-3 text-xs text-gray-650 dark:text-gray-300 leading-relaxed overflow-hidden"
              >
                <p>
                  O <strong>TASK CRM</strong> é um sistema unificado de gerenciamento de relacionamento com clientes desenvolvido para otimizar fluxos comerciais de empresas. A plataforma centraliza a triagem de leads, organização de pipeline no formato Kanban, relatórios de produtividade e controle de múltiplos canais de comunicação.
                </p>
                
                <p>
                  <strong>Finalidade do Uso da API do Google Calendar:</strong> A fim de viabilizar a marcação ágil de reuniões diretamente no painel do CRM, o <strong>TASK CRM</strong> solicita acesso ao <strong>Google Calendar (Google Agenda)</strong> do usuário. Esse acesso é restrito unicamente para:
                </p>
                
                <ul className="list-disc pl-4 space-y-1.5 text-[11px] text-gray-600 dark:text-gray-400">
                  <li>Sincronizar a agenda do vendedor com o CRM para identificar horários comerciais disponíveis.</li>
                  <li>Criar novos eventos de reuniões e demonstrações com leads, enviando notificações automáticas por e-mail.</li>
                  <li>Atualizar ou remarcar compromissos em tempo real, evitando conflitos de horários.</li>
                </ul>

                <p className="text-[11px] text-gray-500 dark:text-gray-500 italic">
                  * Compromisso de Privacidade: Os dados obtidos por meio da API do Google Calendar são processados em conformidade com as diretrizes do Google e nunca são repassados, compartilhados ou comercializados com terceiros.
                </p>
              </motion.div>
            )}
            
            <div className="mt-4 pt-3.5 border-t border-gray-200/80 dark:border-gray-800/80 flex flex-wrap justify-between items-center gap-x-4 gap-y-2 text-xs font-semibold">
              <div className="flex items-center gap-2">
                <Link to="/politica" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Política de Privacidade
                </Link>
                <span className="text-gray-300 dark:text-gray-700 font-normal">•</span>
                <Link to="/termos" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Termos de Uso
                </Link>
              </div>
              <span className="text-gray-400 dark:text-gray-500 font-normal text-[11px]">
                © {new Date().getFullYear()} TASK CRM
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
